// The backoffice theme on the page: writes the legacy CSS variables from @radish/ui-tokens onto :root (light) and
// [data-mode=dark], and resolves the Auto / Light / Dark appearance the legacy user menu offers.
import { useEffect, useState } from 'react';
import { backofficeColors, type BackofficeColors, type BackofficeContrast, type BackofficeScheme } from '@radish/ui-tokens';

export type Appearance = 'auto' | 'light' | 'dark';

const declarations = (colors: BackofficeColors): string =>
  Object.entries(colors).map(([name, value]) => `--${name}:${value};`).join('');

/** The stylesheet text: light on :root, dark under [data-mode="dark"]. AA contrast unless `legacy` is asked for. */
export const themeCss = (contrast: BackofficeContrast = 'aa'): string =>
  `:root{${declarations(backofficeColors('light', contrast))}}[data-mode="dark"]{${declarations(backofficeColors('dark'))}}`;

export function ThemeStyle({ contrast = 'aa' }: { readonly contrast?: BackofficeContrast }) {
  return <style data-radish-theme="backoffice">{themeCss(contrast)}</style>;
}

const prefersDark = (): boolean => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: dark)').matches;

/** Resolves an appearance to a scheme and keeps `data-mode` on <html> in step (Auto follows the OS). */
export function useAppliedScheme(appearance: Appearance): BackofficeScheme {
  const [osDark, setOsDark] = useState(prefersDark);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setOsDark(query.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);
  const scheme: BackofficeScheme = appearance === 'auto' ? (osDark ? 'dark' : 'light') : appearance;
  useEffect(() => {
    document.documentElement.dataset['mode'] = scheme;
  }, [scheme]);
  return scheme;
}
